import { BuyerTeamEditor } from '@dataroom/shared';
import type { AdminBuyerTeamResponse, TeamRole } from '@dataroom/shared';

/**
 * The "Team" section of the admin's buyer detail (migrations 0070 + 0075): the
 * people on this buyer's team — colleagues and outside advisers of any email
 * domain — with add by email, remove, and the buyer's waiting asks to approve
 * or decline (with the reason when one can't be approved). Whoever is on it keeps access with the buyer during an
 * exclusivity; everyone else is paused. When this person is themselves on
 * another buyer's team, that is said instead (only the lead has a team).
 * Presentational — the page owns the queries and mutations.
 */
export function BuyerTeamSection({
  team,
  loading,
  error,
  adding,
  removingId,
  actionError,
  decidingId = null,
  settingRolesId = null,
  onAdd,
  onRemove,
  onSetRoles,
  onApprove,
  onDecline,
}: {
  team: AdminBuyerTeamResponse | undefined;
  loading: boolean;
  error: string | null;
  adding: boolean;
  removingId: string | null;
  actionError: string | null;
  onAdd: (email: string, roles: TeamRole[], expiresAt?: string) => Promise<unknown>;
  onRemove: (memberId: string) => void;
  /** Change a member's roles (Legal, Financial, ...). */
  onSetRoles?: (memberId: string, roles: TeamRole[]) => void;
  settingRolesId?: string | null;
  /** The buyer's waiting asks: approve (added + invited) or decline. */
  decidingId?: string | null;
  onApprove?: (requestId: string, expiresAt: string) => void;
  onDecline?: (requestId: string) => void;
}) {
  const memberOf = team?.memberOf ?? [];
  return (
    <section aria-label="Team" className="space-y-2 rounded-md border p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Team</p>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : memberOf.length > 0 ? (
        <p className="text-sm">
          On the team of <strong>{memberOf.map((l) => l.email).join(', ')}</strong>. They see what
          that buyer sees and keep access with them. Change the team from that buyer.
        </p>
      ) : (
        <BuyerTeamEditor
          members={team?.members ?? []}
          requests={team?.requests ?? []}
          viewer="admin"
          adding={adding}
          removingId={removingId}
          decidingId={decidingId}
          settingRolesId={settingRolesId}
          error={actionError}
          onAdd={onAdd}
          onRemove={onRemove}
          onSetRoles={onSetRoles}
          onApprove={onApprove}
          onDecline={onDecline}
        />
      )}
    </section>
  );
}
